import React, { useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import styled from 'styled-components';
import { FocusableSection, FocusableButton } from '../components/Focusable';
import { API_URL } from '../config';
import { Html5Qrcode } from 'html5-qrcode';

const Container = styled.div`
  padding: 2rem;
  max-width: 450px;
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 1rem;
    justify-content: flex-start;
    padding-top: 3rem;
  }
`;

const Card = styled.div`
  background: linear-gradient(145deg, #1a1d24, #15171c);
  border-radius: 16px;
  padding: 2.5rem;
  width: 100%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(99, 44, 228, 0.2);

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 12px;
  }
`;

const Title = styled.h1`
  color: #fff;
  text-align: center;
  margin-bottom: 2rem;
  font-size: 2rem;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  background: rgba(0, 0, 0, 0.3);
  padding: 0.25rem;
  border-radius: 8px;
`;

const Tab = styled.button`
  flex: 1;
  background: ${props => props.active ? '#632ce4' : 'transparent'};
  color: #fff;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.active ? '#5020c0' : 'rgba(99, 44, 228, 0.2)'};
  }
`;

const Form = styled.form`
  width: 100%;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.875rem;
  margin: 0.75rem 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;

  &::placeholder {
    color: #666;
  }

  &:focus {
    border-color: #632ce4;
    background: rgba(99, 44, 228, 0.1);
  }
`;

const Button = styled.button`
  width: 100%;
  color: #fff;
  text-decoration: none;
  padding: 1rem;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #632ce4, #9d50bb);
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  font-size: 1.1rem;
  font-weight: 600;
  box-shadow: 0 4px 15px rgba(99, 44, 228, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(99, 44, 228, 0.5);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const QrButton = styled.button`
  width: 100%;
  color: #fff;
  padding: 0.875rem;
  border: 1px solid rgba(99, 44, 228, 0.4);
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1.5rem;
  font-size: 1rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    background: rgba(99, 44, 228, 0.15);
    border-color: #632ce4;
  }
`;

const Error = styled.div`
  background: rgba(231, 76, 60, 0.1);
  border: 1px solid rgba(231, 76, 60, 0.3);
  color: #e74c3c;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  text-align: center;
`;

const Success = styled.div`
  background: rgba(39, 174, 96, 0.1);
  border: 1px solid rgba(39, 174, 96, 0.3);
  color: #27ae60;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  text-align: center;
`;

const PasswordHint = styled.p`
  color: #888;
  font-size: 0.8rem;
  margin: 0.25rem 0 0.75rem 0;
  font-style: italic;
`;

const ScannerContainer = styled.div`
  width: 100%;
  text-align: center;
`;

const ScannerView = styled.div`
  width: 100%;
  margin: 1rem auto;
  border-radius: 12px;
  border: 2px solid rgba(99, 44, 228, 0.4);

  & video {
    border-radius: 10px;
  }
`;

const ScannerHint = styled.p`
  color: #888;
  font-size: 0.9rem;
  margin: 1rem 0;
`;

const CancelButton = styled.button`
  background: transparent;
  border: 1px solid #666;
  color: #fff;
  padding: 0.75rem 2rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  margin-top: 1rem;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin-top: 1.5rem;
  gap: 1rem;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const DividerText = styled.span`
  color: #666;
  font-size: 0.85rem;
`;

const Login = ({ openSidebar, setCurrentPage }) => {
  const [activeTab, setActiveTab] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const { login } = useContext(AuthContext);
  const html5QrCodeRef = useRef(null);
  const scannerContainerId = 'qr-reader';

  // Cleanup scanner on unmount
  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (state === 2) { // SCANNING
          await html5QrCodeRef.current.stop();
        }
        html5QrCodeRef.current.clear();
      } catch (err) {
        // Ignore cleanup errors
      }
      html5QrCodeRef.current = null;
    }
  };

  const startScanner = async () => {
    setShowScanner(true);
    setError('');

    // Attendre que le DOM soit pret
    setTimeout(async () => {
      try {
        const html5QrCode = new Html5Qrcode(scannerContainerId);
        html5QrCodeRef.current = html5QrCode;

        await html5QrCode.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: (viewfinderWidth, viewfinderHeight) => {
              const size = Math.min(viewfinderWidth, viewfinderHeight) * 0.7;
              return { width: size, height: size };
            },
            aspectRatio: 1,
          },
          async (decodedText) => {
            // QR code detecte
            await stopScanner();
            setShowScanner(false);
            handleQrLogin(decodedText);
          },
          () => {
            // Scan en cours (pas de QR detecte)
          }
        ).catch(async (err) => {
          // Fallback : essayer la camera frontale si la camera arriere n'est pas disponible
          console.warn('Camera arriere non disponible, essai camera frontale:', err);
          try {
            await html5QrCode.start(
              { facingMode: 'user' },
              {
                fps: 10,
                qrbox: (viewfinderWidth, viewfinderHeight) => {
                  const size = Math.min(viewfinderWidth, viewfinderHeight) * 0.7;
                  return { width: size, height: size };
                },
                aspectRatio: 1,
              },
              async (decodedText) => {
                await stopScanner();
                setShowScanner(false);
                handleQrLogin(decodedText);
              },
              () => {}
            );
          } catch (err2) {
            throw err2;
          }
        });
      } catch (err) {
        console.error('Erreur demarrage scanner:', err);
        setError('Impossible d\'acceder a la camera. Verifiez les permissions.');
        setShowScanner(false);
      }
    }, 100);
  };

  const handleCancelScan = async () => {
    await stopScanner();
    setShowScanner(false);
  };

  const handleQrLogin = async (qrToken) => {
    setLoading(true);
    setError('');

    try {
      const res = await axios.post(`${API_URL}/auth/qr-login`, { qr_token: qrToken });
      login(res.data.token);
      openSidebar();
      setCurrentPage('home');
    } catch (err) {
      setError(err.response?.data?.error || 'QR Code invalide ou expire');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/login`, { email, password });
      login(res.data.token);
      openSidebar();
      setCurrentPage('home');
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!prenom.trim() || !nom.trim()) {
      setError('Prénom et nom sont requis');
      setLoading(false);
      return;
    }

    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      setLoading(false);
      return;
    }

    try {
      await axios.post(`${API_URL}/register`, { email, password, prenom: prenom.trim(), nom: nom.trim() });
      setSuccess('Inscription réussie ! Vous pouvez maintenant vous connecter.');
      setEmail('');
      setPassword('');
      setPrenom('');
      setNom('');

      // Basculer vers l'onglet connexion après 2 secondes
      setTimeout(() => {
        setActiveTab('login');
        setSuccess('');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setError('');
    setSuccess('');
    setEmail('');
    setPassword('');
    setPrenom('');
    setNom('');
  };

  return (
    <FocusableSection focusKey="LOGIN_PAGE">
      <Container>
        <Card>
          <Title>Bienvenue sur Famibox</Title>

          {showScanner ? (
            <ScannerContainer>
              <ScannerHint>Presentez le QR Code devant la camera</ScannerHint>
              {error && <Error>{error}</Error>}
              <ScannerView>
                <div id={scannerContainerId}></div>
              </ScannerView>
              <CancelButton onClick={handleCancelScan}>
                Annuler
              </CancelButton>
            </ScannerContainer>
          ) : (
            <>
              <TabContainer>
                <FocusableButton
                  as={Tab}
                  active={activeTab === 'login'}
                  onPress={() => handleTabChange('login')}
                >
                  Connexion
                </FocusableButton>
                <FocusableButton
                  as={Tab}
                  active={activeTab === 'register'}
                  onPress={() => handleTabChange('register')}
                >
                  Inscription
                </FocusableButton>
              </TabContainer>

              {error && <Error>{error}</Error>}
              {success && <Success>{success}</Success>}

              {activeTab === 'login' ? (
                <>
                  <Form onSubmit={handleLogin}>
                    <Input
                      type="email"
                      placeholder="Adresse email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !loading) {
                          e.preventDefault();
                          e.stopPropagation();
                          handleLogin(e);
                        }
                      }}
                      required
                      disabled={loading}
                    />
                    <Input
                      type="password"
                      placeholder="Mot de passe"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !loading) {
                          e.preventDefault();
                          e.stopPropagation();
                          handleLogin(e);
                        }
                      }}
                      required
                      disabled={loading}
                    />
                    <Button type="submit" disabled={loading}>
                      {loading ? 'Connexion...' : 'Se connecter'}
                    </Button>
                  </Form>

                  <Divider>
                    <DividerText>ou</DividerText>
                  </Divider>

                  <FocusableButton
                    as={QrButton}
                    onPress={startScanner}
                    disabled={loading}
                  >
                    Scanner un QR Code
                  </FocusableButton>
                </>
              ) : (
                <Form onSubmit={handleRegister}>
                  <Input
                    type="text"
                    placeholder="Prénom"
                    value={prenom}
                    onChange={(e) => setPrenom(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !loading) {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRegister(e);
                      }
                    }}
                    required
                    disabled={loading}
                  />
                  <Input
                    type="text"
                    placeholder="Nom"
                    value={nom}
                    onChange={(e) => setNom(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !loading) {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRegister(e);
                      }
                    }}
                    required
                    disabled={loading}
                  />
                  <Input
                    type="email"
                    placeholder="Adresse email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !loading) {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRegister(e);
                      }
                    }}
                    required
                    disabled={loading}
                  />
                  <Input
                    type="password"
                    placeholder="Mot de passe (min. 8 caractères)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !loading) {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRegister(e);
                      }
                    }}
                    required
                    disabled={loading}
                  />
                  <PasswordHint>
                    Le mot de passe doit contenir au moins 8 caractères
                  </PasswordHint>
                  <Button type="submit" disabled={loading}>
                    {loading ? 'Inscription...' : 'S\'inscrire'}
                  </Button>
                </Form>
              )}
            </>
          )}
        </Card>
      </Container>
    </FocusableSection>
  );
};

export default Login;
